import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from "react";
import { formField } from "@web/theme/theme";
import { Text } from "./Text";

export type FieldA11y = { id: string; "aria-invalid"?: boolean; "aria-describedby"?: string };

export type FormFieldProps = {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  className?: string;
  /** The control: an element accepting `id`/`aria-*`, or a render function for controls wrapped in extra markup. */
  children: ReactElement<Partial<FieldA11y>> | ((a11y: FieldA11y) => ReactNode);
};

/** Label + control + hint/error (SPEC §6.2). Wires `htmlFor`/`aria-describedby` onto the child control. */
export function FormField({ label, hint, error, className = "", children }: FormFieldProps) {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  const a11y: FieldA11y = { id, "aria-invalid": error ? true : undefined, "aria-describedby": describedBy };
  const control = typeof children === "function" ? children(a11y) : isValidElement(children) ? cloneElement(children, a11y) : children;
  return (
    <div className={`${formField.wrap} ${className}`.trim()}>
      <label htmlFor={id} className={formField.label}>
        {label}
      </label>
      {control}
      {error ? (
        <Text as="p" variant="caption" tone="danger">
          <span id={`${id}-error`}>{error}</span>
        </Text>
      ) : (
        hint && (
          <Text as="p" variant="caption" tone="tertiary">
            <span id={`${id}-hint`}>{hint}</span>
          </Text>
        )
      )}
    </div>
  );
}

/** Native textarea styled like an input (client / internal notes). */
export function TextArea({ label, hint, error, className, ...rest }: Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "className" | "id"> & { label: ReactNode; hint?: ReactNode; error?: ReactNode; className?: string }) {
  return (
    <FormField label={label} hint={hint} error={error} className={className}>
      <textarea className={`${formField.control} ${formField.textarea}`} {...rest} />
    </FormField>
  );
}
